import { useState, useEffect, useCallback } from "react";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { PaymentDialog } from "@/components/dialogs/PaymentDialog";
import { DataUnavailable } from "@/components/pwa/OfflineBanner";
import { SearchField } from "@/components/SearchField";
import { useResource } from "@/pwa/useResource";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Trash2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data: pd, unavailable, reload: load } = useResource(useCallback(async () => { const r = await apiGet("/payments"); return { data: r.data, stale: r.stale }; }, []), []);
  const payments = pd || [];
  const { guard } = useOnlineGuard();

  const filtered = payments.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (p.owner_name || "").toLowerCase().includes(q)
      || (p.dog_name || "").toLowerCase().includes(q)
      || (p.notes || "").toLowerCase().includes(q);
  });

  const del = async (p) => {
    try { await api.delete(`/payments/${p.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const isIn = (t) => t === "Pagamento" || t === "Credito cliente";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Pagamenti</h1>
        <Button data-testid="add-payment-btn" onClick={guard(() => setOpen(true))} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Registra pagamento</Button>
      </div>

      <div className="sm:max-w-xs">
        <SearchField value={query} onChange={setQuery} placeholder="Cerca per cliente o cane…" testid="pagamenti-search" />
      </div>

      {unavailable ? <DataUnavailable /> : (
      <div className="space-y-2" data-testid="payments-list">
        {filtered.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
            <span className={`grid place-items-center h-10 w-10 rounded-xl shrink-0 ${isIn(p.type) ? "bg-[#E8F5E9] text-[#1E5E2B]" : "bg-[#FFEBEE] text-[#C62828]"}`}>
              {isIn(p.type) ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.owner_name}{p.dog_name ? ` · ${p.dog_name}` : ""}</div>
              {p.link_label && <div className="text-sm text-foreground/75 truncate">{p.link_label}</div>}
              <div className="text-sm text-muted-foreground">{fmtDate(p.date)} · {p.method} · {p.type}{p.notes ? ` · ${p.notes}` : ""}</div>
            </div>
            <div className="font-head font-extrabold tabular">{fmtEUR(p.amount)}</div>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`pay-del-${p.id}`} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare questo pagamento?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <span className="block">Importo: <b>{fmtEUR(p.amount)}</b></span>
                      <span className="block">Cliente: <b>{p.owner_name || "—"}</b>{p.dog_name ? ` · ${p.dog_name}` : ""}</span>
                      {p.link_label && <span className="block">{p.link_label}</span>}
                      <span className="block mt-2 text-destructive font-medium">Questa operazione modificherà l'incassato e il saldo da incassare.</span>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction data-testid={`pay-del-confirm-${p.id}`} onClick={() => del(p)}>Elimina pagamento</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground" data-testid="payments-empty">{query.trim() ? "Nessun pagamento trovato." : "Nessun pagamento registrato."}</p>}
      </div>
      )}

      <PaymentDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
